import Generic from 'src/models/generic/Generic'
import KitchenDisplayType from 'src/models/kitchen-display/KitchenDisplayType'
import Location from 'src/models/location/Location'
import Model, { encoders, executeMutation, GqlError, ID, Int, Integer, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import Order from 'src/models/order/Order'

export default class KitchenDisplay extends Model {
  static name = 'KitchenDisplay'

  static deletable = true
  static duplicatable = true
  static paginate = true
  static versioned = true

  static isConfiguration = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Kitchen]

  static define () {
    this.concerns = [Archivable, Librariable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String,
        required: true
      },
      kitchenDisplayType: {
        type: KitchenDisplayType,
        encode: encoders.hasOne,
        default: null,
        reference: true
      },
      locations: {
        type: [Location],
        default: () => ([]),
        reference: true
      },
      receiptPaperWidth: {
        type: Integer,
        default: 576
      },
      displayOrder: {
        type: Integer,
        default: 100
      }
    }

    this.queries = {
      kitchenOrder: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          kitchenDisplayId: {
            type: ID,
            required: false
          },
          prepared: {
            type: Boolean,
            required: false
          },
          delivered: {
            type: Boolean,
            required: false
          },
          locationIds: {
            type: [ID],
            required: false
          },
          deliveryTypes: {
            type: [String],
            required: false
          },
          deliveryLocations: {
            type: [String],
            required: false
          }
        },
        results: {
          order: Order
        }
      },
      kitchenOrders: {
        arguments: {
          kitchenDisplayId: {
            type: ID,
            required: false
          },
          prepared: {
            type: Boolean,
            required: false
          },
          delivered: {
            type: Boolean,
            required: false
          },
          locationIds: {
            type: [ID],
            required: false
          },
          deliveryTypes: {
            type: [String],
            required: false
          },
          deliveryLocations: {
            type: [String],
            required: false
          },
          limit: {
            type: Int,
            required: false
          }
        },
        results: {
          orders: [Order]
        }
      },
      kitchenOrderSummary: {
        arguments: {
          kitchenDisplayId: {
            type: ID,
            required: false
          },
          prepared: {
            type: Boolean,
            required: false
          },
          delivered: {
            type: Boolean,
            required: false
          },
          summarizeBy: {
            type: String,
            required: false
          },
          locationIds: {
            type: [ID],
            required: false
          },
          deliveryTypes: {
            type: [String],
            required: false
          },
          deliveryLocations: {
            type: [String],
            required: false
          }
        },
        results: {
          value: Generic
        }
      },
      averageOrderCompletionTime: {
        arguments: {
          kitchenDisplayId: {
            type: ID,
            required: false
          }
        },
        results: {
          value: Generic
        }
      },
      orderDeliveryMetrics: {
        arguments: {
          kitchenDisplayId: {
            type: ID,
            required: false
          }
        },
        results: {
          value: Generic
        }
      }
    }

    this.mutations = {
      updateOrderForKitchenDisplay: {
        arguments: {
          kitchenDisplayId: {
            type: ID,
            required: false
          },
          orderId: {
            type: ID,
            required: true
          },
          orderItemIds: {
            type: [ID],
            required: true
          },
          action: {
            type: String,
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      }
    }
  }

  async updateOrderForKitchenDisplay (orderId, orderItemIds, action) {
    const results = await executeMutation(
      'updateOrderForKitchenDisplay',
      {
        kitchenDisplayId: this.id,
        orderId,
        orderItemIds,
        action
      },
      {
        success: true,
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results.errors
  }
}
